/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-02-14 00:00:00
 *
 *******************************************************/
import _ from 'lodash';

import React, { useState, useCallback, useEffect } from 'react';
import { RadioButton, Banner, Icon, Link } from '@shopify/polaris';
import { ExternalMinor } from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { routes } from 'src/routes';
import { getFullAppUrlV2 } from 'src/helpers';

import ThumbnailDefaultTemplate from 'src/assets/images/shopify-integration/ThumbnailDefaultTemplate.png';
import ThumbnailBasicTemplate from 'src/assets/images/shopify-integration/ThumbnailBasicTemplate.png';
import { MAPPING_THEME_NAME } from 'src/constants/theme-settings';

const BoostTemplatesContent = (props: any | WithI18nProps) => {
  const { i18n, boostTemplates = [], isReachLimitCustomTemplate = false } = props;

  // Get default boost template
  const defaultBoostTemplate = _.find(boostTemplates, { themeNameLib: 'default' }) || {};
  const [value, setValue] = useState(_.get(defaultBoostTemplate, 'templateId', ''));

  // Component did mount
  useEffect(() => {
    const templateInstance = _.find(boostTemplates, { templateId: value });
    const { themeNameLib } = templateInstance;
    props.handleSelectTemplate(value, themeNameLib, true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = useCallback(
    (boostTemplate: any) => {
      const { templateId, themeNameLib } = boostTemplate;
      setValue(templateId);

      props.handleSelectTemplate(templateId, themeNameLib, true);
    },
    [props]
  );

  const renderItem = boostTemplate => {
    const { themeNameLib, templateName } = boostTemplate;

    const thumbnails = {
      default: ThumbnailDefaultTemplate,
      dawn: ThumbnailBasicTemplate
    };

    const displayName = templateName || MAPPING_THEME_NAME[themeNameLib] || themeNameLib;

    return (
      <div className="item">
        <img className="thumbnail" src={thumbnails[themeNameLib]} width={50} height={40} alt="" />
        <div className="details">
          <div className="title">{_.upperFirst(displayName)}</div>
          <div className="description">
            {i18n.translate('AdminHTML.ShopifyIntegration.SelectTemplateModal.createdByBoost')}
          </div>
        </div>

        {/* View demo template */}
        <Link
          external
          onClick={() => {
            const previewURL = getFullAppUrlV2(
              routes.shopifyIntegration.previewBoostTemplate(displayName)
            );
            window.open(previewURL, '_blank');
          }}>
          {'View'}
          <Icon source={ExternalMinor} color="interactive" />
        </Link>
      </div>
    );
  };

  return (
    <div className="modal-content">
      {isReachLimitCustomTemplate && (
        <Banner status="warning">
          <p>{i18n.translate('AdminHTML.ShopifyIntegration.SelectTemplateModal.reachLimit')}</p>
        </Banner>
      )}

      <div className="explore">
        {i18n.translate('AdminHTML.ShopifyIntegration.SelectTemplateModal.exploreContent')}
      </div>

      <ul>
        {boostTemplates.map(boostTemplate => {
          const { templateId } = boostTemplate;

          return (
            <li key={templateId}>
              <RadioButton
                label={''}
                checked={value === templateId}
                id={templateId}
                name="templateId"
                onChange={() => handleChange(boostTemplate)}
              />
              {renderItem(boostTemplate)}
            </li>
          );
        })}
      </ul>

      <div className="coming-soon">
        {i18n.translate('AdminHTML.ShopifyIntegration.SelectTemplateModal.moreTemplatesAddedSoon')}
      </div>
    </div>
  );
};

export default withI18n()(BoostTemplatesContent);
